/**
 * Runtime Environment Configuration Utility
 * 
 * This module provides a centralized way to access runtime environment variables
 * that are injected via window.ENV_CONFIG from the public/config.js file.
 * 
 * Benefits:
 * - Single source of truth for environment configuration
 * - Easier to maintain and update
 * - Better testability (can mock this module)
 * - Consistent fallback values
 * - IDE autocomplete support
 */

export const getEnvConfig = () => {
  // Helper function to check if a value is a valid runtime value (not a placeholder)
  const isValidRuntimeValue = (value) => {
    return value && !value.startsWith('${') && value !== 'undefined';
  };

  // Helper to get the appropriate value: prioritize process.env for local, window.ENV_CONFIG for deployed
  const getValue = (envKey, windowKey, fallback = '') => {
    const processValue = process.env[envKey];
    const windowValue = window.ENV_CONFIG?.[windowKey];
    
    // If process.env has a real value (not a placeholder), use it (local development)
    if (isValidRuntimeValue(processValue)) {
      return processValue;
    }
    
    // Otherwise, use window.ENV_CONFIG if it has a real value (deployed environment)
    if (isValidRuntimeValue(windowValue)) {
      return windowValue;
    }
    
    // Fall back to default
    return fallback;
  };

  return {
    apiBaseUrl: getValue('REACT_APP_API_BASE_URL', 'API_BASE_URL', 'http://localhost:8000'),
    azure: {
      tenant: getValue('REACT_APP_AZURE_APP_REGISTRY_TENANT', 'AZURE_APP_REGISTRY_TENANT', ''),
      clientId: getValue('REACT_APP_AZURE_APP_REGISTRY_CLIENT', 'AZURE_APP_REGISTRY_CLIENT', ''),
      scope: getValue('REACT_APP_AZURE_APP_REGISTRY_SCOPE', 'AZURE_APP_REGISTRY_SCOPE', '')
    },
    redirectUri: getValue('REACT_APP_REDIRECT_URI', 'REDIRECT_URI', ''),
    hero: {
      title: getValue('REACT_APP_HERO_SECTION_TITLE', 'HERO_SECTION_TITLE', 'Default Title'),
      description: getValue('REACT_APP_HERO_SECTION_DESCRIPTION', 'HERO_SECTION_DESCRIPTION', ''),
      buttonLabel: getValue('REACT_APP_HERO_SECTION_BUTTON_LABEL', 'HERO_SECTION_BUTTON_LABEL', 'Learn More'),
      buttonUrl: getValue('REACT_APP_HERO_SECTION_BUTTON_URL', 'HERO_SECTION_BUTTON_URL', '#')
    },
    footer: {
      linkedin: getValue('REACT_APP_FOOTER_LINKEDIN', 'FOOTER_LINKEDIN', ''),
      youtube: getValue('REACT_APP_FOOTER_YOUTUBE', 'FOOTER_YOUTUBE', ''),
      instagram: getValue('REACT_APP_FOOTER_INSTAGRAM', 'FOOTER_INSTAGRAM', ''),
      privacyStatement: getValue('REACT_APP_FOOTER_PRIVACY_STATEMENT', 'FOOTER_PRIVACY_STATEMENT', ''),
      cookiePolicy: getValue('REACT_APP_FOOTER_COOKIE_POLICY', 'FOOTER_COOKIE_POLICY', ''),
      termsOfUse: getValue('REACT_APP_FOOTER_TERMS_OF_USE', 'FOOTER_TERMS_OF_USE', ''),
      doNotSellMyInfo: getValue('REACT_APP_FOOTER_DO_NOT_SELL_MY_INFO', 'FOOTER_DO_NOT_SELL_MY_INFO', ''),
      accessibilityStatement: getValue('REACT_APP_FOOTER_ACCESSIBILITY_STATEMENT', 'FOOTER_ACCESSIBILITY_STATEMENT', ''),
      codeOfConduct: getValue('REACT_APP_FOOTER_CODE_OF_CONDUCT', 'FOOTER_CODE_OF_CONDUCT', '')
    },
    organization: getValue('REACT_APP_ORGANIZATION', 'ORGANIZATION', '')
  };
};

/**
 * Legacy compatibility: Provide direct access to commonly used values
 * These can be deprecated once all code uses the structured config
 */
export const config = getEnvConfig();
